import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  YARIKANO_CHAT_AUTO_QUESTION_ID,
  YARIKANO_CHAT_CTA_URL,
  YARIKANO_CHAT_TYPING_MS,
  getYarikanoChatItem,
} from '../../constants';
import {
  EYarikanoCtaPlacement,
  EYarikanoQuestionId,
  TQuickReplyItem,
  TYarikanoChatSessionState,
} from 'types';
import {
  appendConsumedAndLog,
  areAllManualChipsConsumed,
  buildAnswerLogEntries,
  loadYarikanoChatSession,
  remainingManualChipItems,
  saveYarikanoChatSession,
  shouldAutoSendGroup,
  trackYarikanoChatChipClick,
  trackYarikanoChatCtaClick,
} from 'utils';

const useYarikanoChat = (userId?: number) => {
  const autoStartedRef = useRef(false);
  const respondingRef = useRef(false);
  const timerRef = useRef<number>();
  const sessionRef = useRef<TYarikanoChatSessionState>(
    loadYarikanoChatSession(),
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [session, setSession] = useState<TYarikanoChatSessionState>(
    () => sessionRef.current,
  );
  const [isResponding, setIsResponding] = useState(false);
  const [inFlightQuestionId, setInFlightQuestionId] =
    useState<EYarikanoQuestionId | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<{
    text: string;
    timetoken: string;
  } | null>(null);

  const persistSession = useCallback((next: TYarikanoChatSessionState) => {
    sessionRef.current = next;
    setSession(next);
    saveYarikanoChatSession(next);
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
        return;
      }
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, []);

  const playAnswerSequence = useCallback(
    async (questionId: EYarikanoQuestionId, fromChip: boolean) => {
      if (respondingRef.current) return;
      if (sessionRef.current.consumedIds.includes(questionId)) return;

      const item = getYarikanoChatItem(questionId);
      if (!item) return;

      respondingRef.current = true;
      setIsResponding(true);
      setInFlightQuestionId(questionId);

      if (fromChip) {
        trackYarikanoChatChipClick({ questionId, userId });
      }

      const startedAtMs = Date.now();
      setPendingQuestion({
        text: item.suggestText,
        timetoken: String(startedAtMs * 10000),
      });
      scrollToBottom();

      await new Promise<void>((resolve) => {
        timerRef.current = window.setTimeout(() => {
          timerRef.current = undefined;
          resolve();
        }, YARIKANO_CHAT_TYPING_MS);
      });

      const entries = buildAnswerLogEntries(item, startedAtMs);
      setPendingQuestion(null);
      persistSession(
        appendConsumedAndLog(sessionRef.current, questionId, entries),
      );
      respondingRef.current = false;
      setIsResponding(false);
      setInFlightQuestionId(null);
      scrollToBottom();
    },
    [persistSession, scrollToBottom, userId],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current !== undefined) {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      // Allow auto-send to retry after Strict Mode remount / real re-entry
      // when the typing wait was cancelled before completion.
      respondingRef.current = false;
      if (shouldAutoSendGroup(sessionRef.current)) {
        autoStartedRef.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    if (!shouldAutoSendGroup(sessionRef.current)) return;
    void playAnswerSequence(YARIKANO_CHAT_AUTO_QUESTION_ID, false);
  }, [playAnswerSequence]);

  useEffect(() => {
    scrollToBottom();
  }, [session.log.length, isResponding, pendingQuestion, scrollToBottom]);

  const remainingChips: TQuickReplyItem[] = useMemo(
    () =>
      remainingManualChipItems(session.consumedIds)
        .filter((item) => item.id !== inFlightQuestionId)
        .map((item) => ({
          id: item.id,
          suggestText: item.suggestText,
        })),
    [inFlightQuestionId, session.consumedIds],
  );

  const showFooterCta =
    areAllManualChipsConsumed(session.consumedIds) && !isResponding;

  const handleChipSelect = useCallback(
    (chip: TQuickReplyItem) => {
      if (isResponding || respondingRef.current) return;
      if (!getYarikanoChatItem(chip.id as EYarikanoQuestionId)) return;
      void playAnswerSequence(chip.id as EYarikanoQuestionId, true);
    },
    [isResponding, playAnswerSequence],
  );

  const handleCtaClick = useCallback(
    (questionId: EYarikanoQuestionId, placement: EYarikanoCtaPlacement) => {
      trackYarikanoChatCtaClick({ questionId, placement, userId });
      window.open(YARIKANO_CHAT_CTA_URL, '_blank', 'noopener,noreferrer');
    },
    [userId],
  );

  const footerQuestionId =
    session.consumedIds[session.consumedIds.length - 1] ??
    YARIKANO_CHAT_AUTO_QUESTION_ID;

  return {
    session,
    pendingQuestion,
    isResponding,
    remainingChips,
    showFooterCta,
    scrollContainerRef,
    bottomRef,
    handleChipSelect,
    handleCtaClick,
    footerQuestionId,
  };
};

export default useYarikanoChat;
